<script setup>
import { ref, onMounted } from 'vue'
import {
  ArrowRight,
  ArrowUpRight,
  Check,
  Smartphone,
  Building2,
  Clock,
  ShieldCheck
} from 'lucide-vue-next'
import heroRider from '../../assets/img/hero-llevo-rider-optimized.webp'
import AudienceSwitcher from './AudienceSwitcher.vue'

const isLoaded = ref(false)

onMounted(() => {
  requestAnimationFrame(() => {
    isLoaded.value = true
  })
})

const trust = [
  { icon: Clock, label: 'Rastreo en tiempo real' },
  { icon: ShieldCheck, label: 'Repartidores verificados' }
]
</script>

<template>
  <section id="inicio" class="relative overflow-hidden bg-[#0a1120] text-white">
    <!-- Fotografía real del servicio: fondo en escritorio -->
    <div class="absolute inset-0 hidden lg:block">
      <img
        :src="heroRider"
        alt="Repartidor LLEVO de noche con bolsa térmica naranja en Managua"
        class="h-full w-full object-cover object-[68%_center]"
        loading="eager"
        fetchpriority="high"
        decoding="async"
      >
      <div class="absolute inset-0 bg-[linear-gradient(90deg,#0a1120_0%,rgba(10,17,32,0.95)_34%,rgba(10,17,32,0.48)_62%,rgba(10,17,32,0.08)_100%)]"></div>
      <div class="absolute inset-0 bg-gradient-to-t from-[#0a1120] via-transparent to-[#0a1120]/35"></div>
    </div>

    <!-- Fondo móvil limpio -->
    <div class="pointer-events-none absolute inset-0 bg-grid-dark opacity-30 lg:hidden"></div>
    <div class="pointer-events-none absolute -right-24 top-16 h-80 w-80 rounded-full bg-[#f0611e]/15 blur-[110px] lg:hidden"></div>

    <div class="container relative z-10 mx-auto px-6 pb-14 pt-32 lg:px-12 lg:pb-16 lg:pt-40">
      <div class="max-w-2xl">
        <h1
          class="mt-6 text-balance text-4xl font-black leading-[1.03] tracking-[-0.035em] text-white transition-all delay-100 duration-700 sm:text-5xl lg:text-[3.8rem] xl:text-[4.2rem]"
          :class="isLoaded ? 'translate-y-0 opacity-100' : 'translate-y-6 opacity-0'"
        >
          Delivery rápido y seguro para tu negocio.
          <span class="relative inline-block text-[#ff8a4c]">
            Y para ti.
            <svg class="absolute -bottom-2 left-0 h-3 w-full text-[#f0611e]/40" viewBox="0 0 100 10" preserveAspectRatio="none" aria-hidden="true">
              <path d="M0 8 Q 50 0, 100 8" stroke="currentColor" stroke-width="4" fill="transparent" />
            </svg>
          </span>
        </h1>

        <p
          class="mt-6 max-w-xl text-base leading-relaxed text-slate-300 transition-all delay-200 duration-700 sm:text-lg"
          :class="isLoaded ? 'translate-y-0 opacity-100' : 'translate-y-6 opacity-0'"
        >
          LLEVO conecta comercios y personas con una red de repartidores propia.
          Pide desde la <strong class="font-semibold text-white">app</strong> o gestiona tus despachos desde el
          <strong class="font-semibold text-white">panel de negocio</strong>, y sigue cada entrega en tiempo real.
        </p>

        <div
          class="mt-8 flex flex-col gap-3 transition-all delay-300 duration-700 sm:flex-row sm:items-center"
          :class="isLoaded ? 'translate-y-0 opacity-100' : 'translate-y-6 opacity-0'"
        >
          <a
            href="#app-usuarios"
            class="group inline-flex items-center justify-center gap-2.5 rounded-xl bg-[#c2410c] px-6 py-3.5 text-base font-bold text-white shadow-orange-glow transition-all duration-200 hover:-translate-y-0.5 hover:bg-[#9a3412]"
          >
            <Smartphone :size="18" />
            Descargar la App
            <ArrowRight :size="17" class="transition-transform group-hover:translate-x-1" />
          </a>
          <a
            href="#negocios"
            class="group inline-flex items-center justify-center gap-2.5 rounded-xl border border-white/20 bg-[#0a1120]/35 px-6 py-3.5 text-base font-bold text-white backdrop-blur transition-all duration-200 hover:-translate-y-0.5 hover:border-white/35 hover:bg-white/10"
          >
            <Building2 :size="18" />
            Registrar mi Negocio
            <ArrowUpRight :size="17" class="text-[#ff8a4c] transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
          </a>
        </div>

        <div
          class="mt-7 flex flex-wrap items-center gap-x-5 gap-y-2 text-xs font-medium text-slate-300 transition-all delay-500 duration-700"
          :class="isLoaded ? 'translate-y-0 opacity-100' : 'translate-y-6 opacity-0'"
        >
          <span v-for="item in trust" :key="item.label" class="inline-flex items-center gap-1.5">
            <component :is="item.icon" :size="14" class="text-[#ff8a4c]" />
            {{ item.label }}
          </span>
        </div>

      </div>

      <!-- Selector swipeable de audiencias -->
      <div
        class="mt-10 transition-all delay-700 duration-1000 lg:mt-16"
        :class="isLoaded ? 'translate-y-0 opacity-100' : 'translate-y-6 opacity-0'"
      >
        <AudienceSwitcher />
      </div>

      <!-- Fotografía real en móvil -->
      <div class="mt-6 overflow-hidden rounded-2xl border border-white/15 lg:hidden">
        <img
          :src="heroRider"
          alt="Repartidor LLEVO de noche con bolsa térmica naranja en Managua"
          class="aspect-[4/3] w-full object-cover object-[72%_center]"
          loading="eager"
          fetchpriority="high"
          decoding="async"
        >
      </div>

      <div class="mt-5 flex items-center justify-center gap-2 text-[10px] font-medium text-slate-400">
        <Check :size="12" class="text-[#ff8a4c]" />
        Tecnología propia, repartidores locales y soporte humano.
      </div>
    </div>
  </section>
</template>
